import type { BackendProsjektRow, ProsjektRow } from "./types";

function normalizeNumber(value: number | null | undefined): number {
    return Number.isFinite(value) ? Number(value) : 0;
}

function slugifyFilterValue(value: string): string {
    return value
        .toLocaleLowerCase("nb")
        .replace(/æ/g, "ae")
        .replace(/ø/g, "o")
        .replace(/å/g, "a")
        .normalize("NFD")
        .replace(/\p{M}/gu, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
}

function mapStatus(status: string | null): string {
    const normalizedStatus = status?.trim();
    if (!normalizedStatus) {
        return "Ukjent";
    }

    if (normalizedStatus === "Prosjektgjennomføring") {
        return "Under gjennomføring";
    }

    return normalizedStatus;
}

function mapAvtaleperiode(startTimestamp: number | null): string {
    if (!startTimestamp) {
        return "Ukjent";
    }

    const year = new Date(startTimestamp).getUTCFullYear();

    if (year <= 2020) {
        return "2017-2020";
    }

    if (year <= 2024) {
        return "2021-2024";
    }

    return "2025-2028";
}

function mapStotteobjekt(row: BackendProsjektRow): string {
    const stotteobjektNavn = row.stotteobjekt_navn?.trim() ?? "";
    const stotteobjektUnderkategori = row.stotteobjekt_underkategori?.trim() ?? "";

    if ((stotteobjektNavn === "Bygg" || stotteobjektNavn === "Kjøretøy") && stotteobjektUnderkategori.length > 0) {
        return stotteobjektUnderkategori;
    }

    return stotteobjektNavn;
}

export function mapBackendRowToProsjektRow(row: BackendProsjektRow): ProsjektRow {
    const kommune = row.prosjektkommune?.trim();
    const fylke = row.prosjektfylke?.trim();
    const prosjektstartTid = row.prosjektstart_gjeldende_tid ?? row.prosjektstart_gjeldende_dato ?? null;
    const prosjektsluttTid = row.prosjektslutt_gjeldende_tid ?? row.prosjektslutt_gjeldende_dato ?? null;
    const stotteobjektHovedkategori = row.stotteobjekt_navn?.trim() ?? "";
    const stotteobjektUnderkategori = row.stotteobjekt_underkategori?.trim() ?? "";
    const levetid = normalizeNumber(row.levetid ?? row.levetid_estimert);
    const utslippsreduksjonPerAr = normalizeNumber(row.klimaresultat_vedtatt);
    const regionValue = kommune
        ? `kommune-${slugifyFilterValue(kommune)}`
        : fylke
          ? `fylke-${slugifyFilterValue(fylke)}`
          : "ukjent";

    return {
        saksnr: row.saksnummer ?? "Ikke oppgitt",
        vedtaksdato: row.vedtaksdato,
        prosjekttittel: row.prosjekttittel ?? "Ikke oppgitt",
        organisasjonsnummer: row.organisasjonsnummer ?? "Ikke oppgitt",
        prosjekteier: row.prosjekteier ?? "Ikke oppgitt",
        stottebelop: normalizeNumber(row.stottebelop_vedtatt),
        stottebelopOpprinnelig: normalizeNumber(row.stottebelop_vedtatt_opprinnelig),
        stotteobjekter: normalizeNumber(row.stotteobjekt_antall),
        levetid,
        utslippsreduksjon: utslippsreduksjonPerAr * levetid,
        energiresultat: normalizeNumber(row.energiresultat_vedtatt),
        stotteobjektHovedkategori,
        stotteobjektUnderkategori,
        prosjektstart_gjeldende_tid: prosjektstartTid,
        prosjektslutt_gjeldende_tid: prosjektsluttTid,
        Region: regionValue,
        prosjektkommune: kommune ?? null,
        prosjektfylke: fylke ?? null,
        Sektor: row.sektor?.trim() ? slugifyFilterValue(row.sektor) : "",
        Støtteobjekt: mapStotteobjekt(row),
        Støtteprogram: row.stotteprogram?.trim() ?? "",
        Status: mapStatus(row.status),
        Avtaleperiode: mapAvtaleperiode(prosjektstartTid),
        url: row.url,
    };
}
